<template>
    <div class="platform-detail-page" v-loading="loading || searchLoading">
        <div v-if="pageMode === 'detail'">
            <el-card shadow="never" class="info-card">
                <div class="card-header">
                    <div class="platform-title-row">
                        <div class="platform-logo" :class="platformClass">
                            <svg-icon v-if="platformType === 'apple'" icon-class="new-appleMusic"></svg-icon>
                            <svg-icon v-else icon-class="new-spotify"></svg-icon>
                        </div>
                        <span class="platform-name-text">{{ platformName }}艺人</span>
                    </div>
                    <div class="badge" :class="`badge-${statusBadgeType}`">{{ statusBadgeText }}</div>
                </div>
                <div class="info-rows">
                    <div class="info-row-two-col">
                        <div class="info-item">
                            <span class="info-label">平台艺人 ID</span>
                            <span class="info-value">
                                {{ platformArtistId || '-' }}
                                <el-button v-if="platformArtistId" type="text" class="copy-btn" @click="copyId">复制</el-button>
                            </span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">平台主页</span>
                            <span v-if="platformArtistId" class="info-value link" @click="openHomePage">打开{{ platformName }}主页 ↗</span>
                            <span v-else class="info-value">-</span>
                        </div>
                    </div>
                    <div class="info-row-two-col">
                        <div class="info-item">
                            <span class="info-label">关联时间</span>
                            <span class="info-value">{{ linkedAt || '-' }}</span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">最近同步时间</span>
                            <span class="info-value">{{ syncedAt || '-' }}</span>
                        </div>
                    </div>
                </div>

                <div class="platform-info-section">
                    <div class="section-title">平台信息</div>
                    <div v-if="hasPlatformInfo" class="profile-content-wrapper">
                        <div class="profile-content">
                            <div class="avatar-wrapper">
                                <el-image
                                    v-if="platformInfo.avatar"
                                    :src="platformInfo.avatar"
                                    fit="cover"
                                    class="avatar profile-avatar"
                                    :preview-src-list="[platformInfo.avatar]"
                                    :z-index="4000"
                                >
                                    <div slot="error" class="avatar-placeholder profile-placeholder">
                                        <i class="el-icon-picture-outline"></i>
                                        <span class="placeholder-text">暂无图片</span>
                                    </div>
                                </el-image>
                                <div v-else class="avatar-placeholder profile-placeholder">
                                    <i class="el-icon-picture-outline"></i>
                                    <span class="placeholder-text">暂无图片</span>
                                </div>
                            </div>
                            <div class="profile-info">
                                <div class="artist-name-row">
                                    <span class="artist-name">{{ platformInfo.name || '-' }}</span>
                                </div>
                                <div v-if="platformTypeCode !== 3 && platformInfo.intro" class="artist-desc">{{ platformInfo.intro }}</div>
                                <div v-if="platformInfo.representative && platformInfo.representative.length" class="tags-line">
                                    <span class="tags-label">{{ platformTypeCode === 3 || platformTypeCode === 4 ? '专辑名称' : '代表作' }}：</span>
                                    <span v-for="(tag, idx) in platformInfo.representative" :key="idx" class="info-tag">
                                        <el-tag type="info">{{ tag }}</el-tag>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="platformTypeCode === 4 && bindStatus === 1 && platformArtistId" class="unsync-hint-box">
                        <div class="unsync-hint-title">尚未同步平台信息</div>
                        <div class="unsync-hint-desc">当前已保存 Spotify 艺人 ID，可点击"同步平台信息"获取头像、名称和专辑等信息。</div>
                    </div>
                    <div v-else-if="isStatusBound" class="unsync-list">
                        <div class="unsync-row">
                            <span class="unsync-label">艺人头像</span>
                            <span class="unsync-value">未同步</span>
                        </div>
                        <div class="unsync-row">
                            <span class="unsync-label">艺人名称</span>
                            <span class="unsync-value">未同步</span>
                        </div>
                        <div class="unsync-row">
                            <span class="unsync-label">专辑</span>
                            <span class="unsync-value">未同步</span>
                        </div>
                        <div class="unsync-row">
                            <span class="unsync-label">粉丝数量</span>
                            <span class="unsync-value">未同步</span>
                        </div>
                    </div>
                    <div v-else class="empty-hint">
                        暂无关联信息
                    </div>
                </div>
            </el-card>

            <div class="actions-bar">
                <el-button @click="goBack">返回</el-button>
                <template v-if="isStatusBound">
                    <el-button type="danger" plain @click="handleUnbind">解除关联</el-button>
                    <el-button v-if="!hideSyncButton" plain @click="handleSync" :loading="syncLoading">同步平台信息</el-button>
                    <el-button type="primary" @click="showRelinkConfirm">重新关联</el-button>
                </template>
                <template v-else>
                    <el-button type="primary" @click="goToBind">去关联</el-button>
                </template>
            </div>
        </div>

        <div v-else>
            <el-card shadow="never" class="bind-card">
                <div class="search-tabs">
                    <div class="tab-item" :class="{active: searchTab === 'name'}" @click="switchTab('name')">
                        名称搜索关联
                    </div>
                    <div class="tab-item" :class="{active: searchTab === 'id'}" @click="switchTab('id')">
                        ID 直接关联
                    </div>
                    <div v-if="platformTypeCode === 4" class="tab-item" :class="{active: searchTab === 'create'}" @click="switchTab('create')">
                        新建主页
                    </div>
                </div>

                <!-- 新建主页（仅Spotify）：不显示搜索功能 -->
                <div v-if="searchTab === 'create'" class="create-main-page">
                    <div class="create-title">让{{ platformName }}新创建主页</div>
                    <div class="create-tip">
                        注意：专辑上线后，请及时关联新创建的主页id，<br />
                        否则随后的新专辑/歌曲上线时，{{ platformName }}可能会再次新建不同主页
                    </div>
                </div>

                <!-- 搜索功能仅在名称搜索和ID关联tab显示 -->
                <template v-else>
                    <div class="search-row">
                        <el-input
                            v-model="searchKeyword"
                            :placeholder="searchTab === 'id' ? `请输入${platformName}艺人ID` : '请输入艺人名称'"
                            class="search-input"
                            clearable
                            @keyup.enter.native="handleSearch"
                        ></el-input>
                        <el-button type="primary" class="search-btn" @click="handleSearch">
                            {{ searchTab === 'id' ? '查询' : '搜索' }}
                        </el-button>
                    </div>

                    <div v-if="searchError" class="error-warn">
                        <div class="error-text">
                            <i class="el-icon-warning-outline"></i>
                            {{ searchError }}
                        </div>
                        <div class="error-actions">
                            <el-button size="small" @click="handleSearch">重试</el-button>
                            <el-button v-if="platformTypeCode === 3" size="small" class="create-home-btn" @click="openCreateHomeDialog">
                                为该艺人新建主页
                            </el-button>
                        </div>
                    </div>

                    <div v-else-if="searchTab === 'id' && searchResult" class="results-list">
                        <div v-if="platformTypeCode === 3" class="create-home-hint-link" @click="openCreateHomeDialog">
                            搜索中没有你要找的艺人？
                            <span class="create-link-text">创建新的主页</span>
                        </div>
                        <div class="result-item active" :class="{'curated-item': searchResult.isCurated}">
                            <div class="result-left-wrapper">
                                <div class="result-left">
                                    <div class="custom-radio checked">
                                        <div class="radio-dot"></div>
                                    </div>
                                    <div class="candidate-avatar">
                                        <el-image v-if="searchResult.avatar" :src="searchResult.avatar" fit="cover">
                                            <div slot="error" class="avatar-placeholder-small">
                                                <i class="el-icon-picture-outline"></i>
                                            </div>
                                        </el-image>
                                        <div v-else class="avatar-placeholder-small">
                                            <i class="el-icon-picture-outline"></i>
                                        </div>
                                    </div>
                                    <div class="candidate-info">
                                        <div class="candidate-name-row">
                                            <span class="candidate-name">{{ searchResult.name }}</span>
                                            <el-tag v-if="platformTypeCode === 3 && searchResult.isCurated" type="danger" class="curated-tag">
                                                <svg-icon icon-class="icon-music-b"></svg-icon>
                                                <span class="tag-text">知名/认证艺人</span>
                                            </el-tag>
                                        </div>
                                        <div class="candidate-sub">{{ platformName }} ID：{{ searchResult.id }}</div>
                                        <div v-if="searchResult.representative && searchResult.representative.length" class="tags-line-small">
                                            <span class="tags-label-small">
                                                {{ platformTypeCode === 3 || platformTypeCode === 4 ? '专辑名称' : '代表作' }}：
                                            </span>
                                            <span v-for="(tag, idx) in searchResult.representative" :key="idx" class="info-tag-small">
                                                <el-tag type="info">{{ tag }}</el-tag>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <el-button v-if="searchResult.url" type="text" class="open-home-btn" @click.stop="openUrl(searchResult.url)">
                                    打开主页
                                </el-button>
                            </div>
                            <div v-if="platformTypeCode === 3 && searchResult.isCurated" class="curated-warning">
                                <i class="el-icon-warning-outline warning-icon"></i>
                                <span>
                                    这是Apple Music已认证的知名艺人。请确认你分发的专辑艺人就是同一个人--关联错误会导致专辑在Apple Music上被隐藏。
                                </span>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="searchTab === 'name' && searchResults.length" class="results-list">
                        <div v-if="platformTypeCode === 3" class="create-home-hint-link" @click="openCreateHomeDialog">
                            搜索中没有你要找的艺人？
                            <span class="create-link-text">创建新的主页</span>
                        </div>
                        <div
                            v-for="(item, idx) in searchResults"
                            :key="idx"
                            class="result-item"
                            :class="{active: selectedResult === idx, 'curated-item': item.isCurated && selectedResult === idx}"
                            @click="selectedResult = idx"
                        >
                            <div class="result-left-wrapper">
                                <div class="result-left">
                                    <div class="custom-radio" :class="{checked: selectedResult === idx}">
                                        <div v-if="selectedResult === idx" class="radio-dot"></div>
                                    </div>
                                    <div class="candidate-avatar">
                                        <el-image v-if="item.avatar" :src="item.avatar" fit="cover">
                                            <div slot="error" class="avatar-placeholder-small">
                                                <i class="el-icon-picture-outline"></i>
                                            </div>
                                        </el-image>
                                        <div v-else class="avatar-placeholder-small">
                                            <i class="el-icon-picture-outline"></i>
                                        </div>
                                    </div>
                                    <div class="candidate-info">
                                        <div class="candidate-name-row">
                                            <span class="candidate-name">{{ item.name }}</span>
                                            <el-tag v-if="platformTypeCode === 3 && item.isCurated" type="danger" class="curated-tag">
                                                <svg-icon icon-class="icon-music-b"></svg-icon>
                                                <span class="tag-text">知名/认证艺人</span>
                                            </el-tag>
                                        </div>
                                        <div class="candidate-sub">{{ platformName }} ID：{{ item.id }}</div>
                                        <div v-if="item.representative && item.representative.length" class="tags-line-small">
                                            <span class="tags-label-small">
                                                {{ platformTypeCode === 3 || platformTypeCode === 4 ? '专辑名称' : '代表作' }}：
                                            </span>
                                            <span v-for="(tag, tagIdx) in item.representative" :key="tagIdx" class="info-tag-small">
                                                <el-tag type="info">{{ tag }}</el-tag>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <el-button v-if="item.url" type="text" class="open-home-btn" @click.stop="openUrl(item.url)">打开主页</el-button>
                            </div>
                            <div v-if="platformTypeCode === 3 && item.isCurated && selectedResult === idx" class="curated-warning">
                                <i class="el-icon-warning-outline warning-icon"></i>
                                <span>
                                    这是Apple Music已认证的知名艺人。请确认你分发的专辑艺人就是同一个人--关联错误会导致专辑在Apple Music上被隐藏。
                                </span>
                            </div>
                        </div>

                        <!-- 加载更多按钮：使用hasMoreResults判断 -->
                        <div v-if="searchTab === 'name' && hasMoreResults" class="load-more-wrapper">
                            <el-button plain :loading="loadMoreLoading" @click="handleLoadMore">加载更多</el-button>
                        </div>
                        <!-- 没有更多了文本 -->
                        <div v-else-if="searchTab === 'name' && showNoMoreText" class="no-more-wrapper">
                            没有更多了
                        </div>
                    </div>
                </template>
            </el-card>

            <div class="actions-bar">
                <el-button @click="cancelSearch">取消</el-button>
                <el-button v-if="searchTab === 'create'" type="primary" :loading="submitLoading" @click="confirmCreate">
                    确认创建
                </el-button>
                <el-button v-else type="primary" :loading="submitLoading" @click="confirmCandidate">
                    确认关联
                </el-button>
            </div>
        </div>

        <el-dialog title="重新关联" :visible.sync="relinkDialogVisible" width="500px" :append-to-body="true" class="relink-dialog">
            <div class="dialog-content">重新关联将替换当前{{ platformName }}艺人 ID，是否继续？</div>
            <div slot="footer">
                <el-button @click="relinkDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="continueRelink">继续</el-button>
            </div>
        </el-dialog>

        <el-dialog title="确认替换关联" :visible.sync="replaceDialogVisible" width="500px" :append-to-body="true">
            <div class="dialog-content">
                确认将{{ platformName }} ID 从
                <br />
                <strong>{{ platformArtistId }}</strong>
                <br />
                替换为
                <br />
                <strong>{{ newId }}</strong>
                吗？
            </div>
            <div slot="footer">
                <el-button @click="replaceDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="finalConfirm">确认替换</el-button>
            </div>
        </el-dialog>

        <el-dialog title="确认关联" :visible.sync="linkConfirmDialogVisible" width="500px" :append-to-body="true">
            <div class="dialog-content">是否确认关联{{ platformName }}？</div>
            <div slot="footer">
                <el-button @click="linkConfirmDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="confirmLink">确认</el-button>
            </div>
        </el-dialog>

        <el-dialog title="新建主页" :visible.sync="createHomeDialogVisible" width="500px" :append-to-body="true" :close-on-click-modal="false">
            <div class="dialog-content">
                确定为
                <strong>{{ searchKeyword }}</strong>
                新建主页吗？
            </div>
            <div slot="footer">
                <el-button @click="createHomeDialogVisible = false">取消</el-button>
                <el-button type="primary" :loading="createHomeLoading" @click="createAndRelate">创建并关联</el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import {getPlatformArtistBindingDetail, syncPlatformArtist, unrelatePlatformArtist, searchPlatformArtist, relatePlatformArtist, markCreatePlatformArtist, createRelAppleMusicArtist} from '@/api/album';

const PLATFORM_TYPE_MAP = {
    apple: 3,
    spotify: 4
};

export default {
    props: {
        artistId: {
            type: [Number, String],
            required: true
        },
        platformType: {
            type: String,
            required: true,
            validator: val => ['apple', 'spotify'].includes(val)
        },
        isDrawer: {
            type: Boolean,
            default: false
        },
        defaultBindMode: {
            type: Boolean,
            default: false
        },
        artistName: {
            type: String,
            default: ''
        },
        hideSyncButton: {
            type: Boolean,
            default: false
        },
        displayLangCode: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            loading: false,
            pageMode: 'detail',
            isBound: false,
            bindStatus: 0,
            platformArtistId: '',
            platformUrl: '',
            linkedAt: '',
            syncedAt: '',
            hasPlatformInfo: false,
            platformInfo: {
                avatar: '',
                name: '',
                fansCount: 0,
                albumCount: 0,
                albums: []
            },
            isCurated: false,
            syncLoading: false,
            searchTab: 'name',
            searchKeyword: '',
            noMoreData: false,
            searchLoading: false,
            hasSearched: false,
            searchResult: null,
            searchResults: [],
            selectedResult: -1,
            currentPage: 1,
            pageSize: 3,
            totalCount: 0,
            totalPage: 0,
            nextCursor: '',
            loadMoreLoading: false,
            searchError: '',
            submitLoading: false,
            relinkDialogVisible: false,
            replaceDialogVisible: false,
            linkConfirmDialogVisible: false,
            createHomeDialogVisible: false,
            createHomeLoading: false,
            newId: ''
        };
    },
    computed: {
        platformName() {
            return this.platformType === 'apple' ? 'Apple Music' : 'Spotify';
        },
        platformClass() {
            return this.platformType === 'apple' ? 'apple-logo' : 'spotify-logo';
        },
        platformTypeCode() {
            return PLATFORM_TYPE_MAP[this.platformType];
        },
        statusBadgeText() {
            if (this.bindStatus === 1) return '已关联';
            if (this.bindStatus === 2) {
                if (this.platformTypeCode === 3) return '知名/认证艺人';
                if (this.platformTypeCode === 4) return '新建主页';
            }
            return '未关联';
        },
        statusBadgeType() {
            if (this.bindStatus === 1) return 'bound';
            if (this.bindStatus === 2) return 'special';
            return 'unbound';
        },
        isStatusBound() {
            // Apple Music：status=1（已关联）和status=2（知名/认证艺人）都显示完整操作按钮
            // Spotify：只有status=1（已关联）显示完整操作按钮
            if (this.platformTypeCode === 3) {
                return this.bindStatus === 1 || this.bindStatus === 2;
            }
            return this.bindStatus === 1;
        },
        pageTitle() {
            return this.pageMode === 'detail' ? `${this.platformName}关联详情` : `关联 ${this.platformName}艺人`;
        },
        hasMoreResults() {
            // Spotify：只要还没确认「没有更多」，一直显示「加载更多」按钮
            // Apple Music：nextCursor存在时显示加载更多
            if (this.platformTypeCode === 3) {
                return !!this.nextCursor;
            }
            return !this.noMoreData;
        },
        showNoMoreText() {
            // 已加载过数据且确认没有更多时返回true
            return this.hasSearched && this.searchResults.length > 0 && this.noMoreData;
        }
    },
    created() {
        this.fetchBindingDetail();
    },
    methods: {
        async fetchBindingDetail() {
            this.loading = true;
            try {
                const res = await getPlatformArtistBindingDetail(this.artistId, this.platformTypeCode);
                if (res.data && res.data.state && res.data.result) {
                    const data = res.data.result;
                    this.platformArtistId = data.platformArtistId || '';
                    this.platformUrl = data.platformUrl || '';
                    this.bindStatus = data.status || 0;
                    // Spotify 新建主页状态（status=2）时关联时间为空
                    const isSpotifyCreate = this.platformTypeCode === 4 && this.bindStatus === 2;
                    this.linkedAt = !isSpotifyCreate && data.addTime ? this.formatDate(data.addTime) : '';
                    this.syncedAt = data.syncTime ? this.formatDate(data.syncTime) : '';
                    // Apple Music：status=1（已关联）和status=2（知名/认证艺人）都是已关联
                    // Spotify：只有status=1（已关联）是已关联
                    if (this.platformTypeCode === 3) {
                        this.isBound = this.bindStatus === 1 || this.bindStatus === 2;
                    } else {
                        this.isBound = this.bindStatus === 1;
                    }

                    if (data.platformInfo) {
                        try {
                            const parsedInfo = typeof data.platformInfo === 'string' ? JSON.parse(data.platformInfo) : data.platformInfo;

                            let name = '';
                            let avatar = '';
                            let representative = [];
                            let fansCount = 0;
                            let intro = '';
                            let members = [];

                            if (this.platformTypeCode === 3) {
                                // Apple Music 字段处理
                                if (parsedInfo.names && parsedInfo.names.length > 0) {
                                    name = parsedInfo.names[0].name || '';
                                }
                                if (!name) name = parsedInfo.name || parsedInfo.artistName || '';
                                avatar = parsedInfo.coverUrl || parsedInfo.avatar || parsedInfo.artworkUrl || '';
                                this.platformUrl = parsedInfo.storeURL || this.platformUrl || '';
                                this.isCurated = parsedInfo.isCurated || data.isCurated || false;
                                if (parsedInfo.titles && parsedInfo.titles.length > 0) {
                                    // 专辑名称最多取5个
                                    representative = parsedInfo.titles
                                        .slice(0, 5)
                                        .map(t => t.name)
                                        .filter(n => n);
                                }
                                // Apple Music 不显示粉丝数、简介、乐队成员
                            } else {
                                // Spotify 通用字段处理
                                name = parsedInfo.name || parsedInfo.artistName || '';
                                // 头像优先从images数组取第一个
                                if (parsedInfo.images && parsedInfo.images.length > 0) {
                                    avatar = parsedInfo.images[0].url || '';
                                }
                                if (!avatar)
                                    avatar =
                                        parsedInfo.coverUrl ||
                                        parsedInfo.avatar ||
                                        parsedInfo.artworkUrl ||
                                        parsedInfo.picUrl ||
                                        parsedInfo.img ||
                                        parsedInfo.image ||
                                        '';
                                fansCount =
                                    parsedInfo.fans !== undefined
                                        ? parsedInfo.fans
                                        : parsedInfo.fansCount || parsedInfo.followers || parsedInfo.followers?.total || 0;
                                intro = parsedInfo.brief || parsedInfo.intro || parsedInfo.briefDesc || '';
                                // 主页链接：externalUrls.spotifyUrl
                                if (parsedInfo.externalUrls && parsedInfo.externalUrls.spotifyUrl) {
                                    this.platformUrl = parsedInfo.externalUrls.spotifyUrl;
                                }
                                // 解析专辑名称：优先从artistAlbum.items取name，最多5个
                                let repWorks = [];
                                if (parsedInfo.artistAlbum && parsedInfo.artistAlbum.items && parsedInfo.artistAlbum.items.length > 0) {
                                    repWorks = parsedInfo.artistAlbum.items
                                        .slice(0, 5)
                                        .map(album => album.name)
                                        .filter(n => n);
                                }
                                // 回退到通用代表作字段
                                if (!repWorks.length) {
                                    repWorks = parsedInfo.works || parsedInfo.representative || parsedInfo.representativeWorks || [];
                                    if (typeof repWorks === 'string') {
                                        repWorks = repWorks
                                            .split(/[、,]/)
                                            .map(item => item.trim())
                                            .filter(item => item);
                                    }
                                }
                                representative = repWorks;
                                // 解析乐队成员
                                let bandMembers = parsedInfo.members || parsedInfo.bandMembers || [];
                                if (typeof bandMembers === 'string') {
                                    bandMembers = bandMembers
                                        .split(/[、,]/)
                                        .map(item => item.trim())
                                        .filter(item => item);
                                }
                                members = bandMembers;
                            }

                            this.platformInfo = {
                                avatar,
                                name,
                                fansCount,
                                intro,
                                representative,
                                members
                            };
                            this.hasPlatformInfo = !!(this.platformInfo.avatar || this.platformInfo.name);
                        } catch (e) {
                            this.hasPlatformInfo = false;
                        }
                    } else {
                        this.hasPlatformInfo = false;
                    }
                } else {
                    this.isBound = false;
                    this.bindStatus = 0;
                    this.platformArtistId = '';
                    this.hasPlatformInfo = false;
                    this.linkedAt = '';
                    this.syncedAt = '';
                    this.platformUrl = '';
                    this.isCurated = false;
                }
            } catch (e) {
                this.isBound = false;
                this.bindStatus = 0;
                this.platformArtistId = '';
                this.hasPlatformInfo = false;
                this.linkedAt = '';
                this.syncedAt = '';
                this.platformUrl = '';
                this.isCurated = false;
            } finally {
                this.loading = false;
                // 如果是drawer模式且需要默认进入绑定模式且未关联，直接进入绑定页面
                if (this.isDrawer && this.defaultBindMode && !this.isBound) {
                    this.$nextTick(() => {
                        this.goToBind();
                    });
                }
            }
        },
        initData() {
            this.fetchBindingDetail();
        },
        formatNumber(num) {
            if (num === undefined || num === null) return '0';
            return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        copyId() {
            const input = document.createElement('input');
            input.value = this.platformArtistId;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
            this.$message.success('ID已复制');
        },
        getHomeUrl() {
            if (this.platformUrl) return this.platformUrl;
            return '';
        },
        openHomePage() {
            const url = this.getHomeUrl();
            if (url) {
                window.open(url);
            }
        },
        goBack() {
            if (this.isDrawer) {
                this.$emit('close');
            } else {
                this.$router.push({name: 'library-artist-info', params: {artistId: this.artistId}});
            }
        },
        async handleSync() {
            this.syncLoading = true;
            try {
                const res = await syncPlatformArtist({
                    artistId: this.artistId,
                    platformType: this.platformTypeCode
                });
                if (!res.data.state) {
                    this.$message.error(res.data.msg || '同步失败');
                    return;
                }
                this.$message.success('同步成功');
                await this.fetchBindingDetail();
            } catch (e) {
                this.$message.error('操作失败');
            } finally {
                this.syncLoading = false;
            }
        },
        async handleUnbind() {
            try {
                await this.$confirm('确定要解除关联吗？', '提示', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消'
                });
            } catch (e) {
                return;
            }

            try {
                const res = await unrelatePlatformArtist({
                    artistId: this.artistId,
                    platformType: this.platformTypeCode
                });
                if (!res.data.state) {
                    this.$message.error(res.data.msg || '解除关联失败');
                    return;
                }
                this.$message.success('已解除关联');
                await this.fetchBindingDetail();
                this.$emit('bind-changed');
            } catch (e) {
                this.$message.error('操作失败');
            }
        },
        showRelinkConfirm() {
            this.relinkDialogVisible = true;
        },
        switchTab(tab) {
            this.searchTab = tab;
            this.searchKeyword = '';
            this.searchResult = null;
            this.searchResults = [];
            this.selectedResult = -1;
            this.currentPage = 1;
            this.totalCount = 0;
            this.totalPage = 0;
            this.nextCursor = '';
            this.noMoreData = false;
            this.searchError = '';
            this.hasSearched = false;
        },
        goToBind() {
            this.pageMode = 'search';
            this.searchTab = 'name';
            this.searchKeyword = this.artistName || '';
            this.searchResult = null;
            this.searchResults = [];
            this.selectedResult = -1;
            this.currentPage = 1;
            this.totalCount = 0;
            this.totalPage = 0;
            this.nextCursor = '';
            this.noMoreData = false;
            this.searchError = '';
            this.hasSearched = false;
            // 如果有艺人名称，自动搜索
            if (this.artistName) {
                this.$nextTick(() => {
                    this.handleSearch(1);
                });
            }
        },
        continueRelink() {
            this.relinkDialogVisible = false;
            this.goToBind();
        },
        cancelSearch() {
            if (this.isDrawer && !this.isBound) {
                this.$emit('close');
            } else {
                this.pageMode = 'detail';
            }
        },
        async handleSearch(page = 1, isLoadMore = false) {
            if (!this.searchKeyword.trim()) {
                this.$message.error(this.searchTab === 'id' ? '请输入艺人ID' : '请输入艺人名称');
                return;
            }
            const targetPage = typeof page === 'number' ? page : 1;
            this.searchLoading = !isLoadMore;
            this.loadMoreLoading = isLoadMore;
            this.searchError = '';
            this.hasSearched = true;
            if (!isLoadMore) {
                this.searchResult = null;
                this.searchResults = [];
                this.selectedResult = -1;
                this.nextCursor = '';
                this.noMoreData = false;
                this.currentPage = targetPage;
            }
            try {
                const params = {
                    platformType: this.platformTypeCode,
                    pageSize: this.searchTab === 'id' ? 1 : this.pageSize
                };
                if (this.searchTab === 'id') {
                    params.platformArtistId = this.searchKeyword.trim();
                    params.pageNo = 1;
                } else {
                    params.keyword = this.searchKeyword.trim();
                    // Apple Music使用cursor分页，其他平台使用pageNo
                    if (this.platformTypeCode === 3) {
                        if (isLoadMore && this.nextCursor) {
                            params.cursor = this.nextCursor;
                        } else {
                            params.pageNo = 1;
                        }
                    } else {
                        params.pageNo = isLoadMore ? targetPage : this.currentPage;
                    }
                }
                const res = await searchPlatformArtist(params);
                if (!res.data.state) {
                    this.searchError = res.data.msg || '暂时无法获取平台艺人信息，请稍后重试。';
                    return;
                }
                const result = res.data.result;
                const list = (result && result.result) || (result && result.list) || [];

                if (this.searchTab === 'id') {
                    if (list.length === 0) {
                        this.searchError = '暂时无法获取平台艺人信息，请稍后重试。';
                        return;
                    }
                    const item = list[0];
                    this.searchResult = this.parsePlatformArtist(item);
                } else {
                    if (!isLoadMore && list.length === 0) {
                        this.searchError = '暂时无法获取平台艺人信息，请稍后重试。';
                        return;
                    }
                    this.totalCount = (result && result.totalCount) || list.length;
                    this.totalPage = (result && result.totalPage) || Math.ceil(this.totalCount / this.pageSize);
                    // 保存nextCursor（Apple Music）
                    this.nextCursor = (result && result.nextCursor) || '';
                    const parsedList = list.map(item => this.parsePlatformArtist(item));
                    if (isLoadMore) {
                        // 加载更多：追加到列表
                        // 当接口返回空列表时，设置noMoreData = true并停止追加
                        if (parsedList.length === 0) {
                            this.noMoreData = true;
                            return;
                        }
                        this.searchResults = this.searchResults.concat(parsedList);
                        // Spotify（pageNo分页）：加载成功后更新当前页码
                        if (this.platformTypeCode !== 3) {
                            this.currentPage = targetPage;
                        }
                        // Apple Music：nextCursor为空时表示没有更多数据
                        if (this.platformTypeCode === 3 && !this.nextCursor) {
                            this.noMoreData = true;
                        }
                    } else {
                        this.searchResults = parsedList;
                        if (this.searchResults.length > 0) {
                            this.selectedResult = 0;
                        }
                        // 第一次搜索后判断是否没有更多数据
                        if (this.platformTypeCode === 3) {
                            // Apple Music：nextCursor为空时表示没有更多数据
                            if (!this.nextCursor) {
                                this.noMoreData = true;
                            }
                        }
                        // Spotify：不提前根据totalPage判断，一直显示「加载更多」按钮，
                        // 直到点击加载更多后接口返回空列表才显示「没有更多了」
                    }
                }
            } catch (e) {
                this.searchError = '暂时无法获取平台艺人信息，请稍后重试。';
            } finally {
                this.searchLoading = false;
                this.loadMoreLoading = false;
            }
        },
        handleLoadMore() {
            this.handleSearch(this.currentPage + 1, true);
        },
        parsePlatformArtist(item) {
            // 根据平台类型解析字段：Apple Music使用appleId/names/titles/storeURL，Spotify使用通用字段
            let id = '';
            let name = '';
            let avatar = '';
            let representative = [];
            let url = '';
            let isCurated = false;

            if (this.platformTypeCode === 3) {
                // Apple Music
                id = item.appleId || item.artistId || item.id || item.platformArtistId || '';
                // names数组取第一个name
                if (item.names && item.names.length > 0) {
                    name = item.names[0].name || '';
                }
                if (!name) name = item.name || item.artistName || '';
                avatar = item.coverUrl || item.avatar || item.artworkUrl || '';
                url = item.storeURL || item.url || '';
                isCurated = item.isCurated || false;
                // titles数组取name作为专辑名称，最多取5个
                if (item.titles && item.titles.length > 0) {
                    representative = item.titles
                        .slice(0, 5)
                        .map(t => t.name)
                        .filter(n => n);
                }
            } else {
                // Spotify (platformType=4)
                id = item.id || item.artistId || item.platformArtistId || '';
                name = item.name || item.artistName || '';
                // images数组取第一个url作为头像
                if (item.images && item.images.length > 0) {
                    avatar = item.images[0].url || '';
                }
                if (!avatar) avatar = item.coverUrl || item.avatar || item.artworkUrl || item.img || item.image || '';
                // externalUrls下的spotifyUrl作为主页链接
                if (item.externalUrls && item.externalUrls.spotifyUrl) {
                    url = item.externalUrls.spotifyUrl;
                } else {
                    url = item.url || '';
                }
                // 专辑名称：优先从artistAlbum.items取name，最多5个
                if (item.artistAlbum && item.artistAlbum.items && item.artistAlbum.items.length > 0) {
                    representative = item.artistAlbum.items
                        .slice(0, 5)
                        .map(album => album.name)
                        .filter(n => n);
                } else {
                    representative = item.works || item.representative || item.representativeWorks || [];
                }
            }

            return {id, name, avatar, representative, url, isCurated};
        },
        async confirmCreate() {
            try {
                this.submitLoading = true;
                const res = await markCreatePlatformArtist({
                    ids: this.artistId,
                    platformType: Number(this.platformTypeCode)
                });
                if (res.data && !res.data.state) {
                    this.$message.error(res.data.msg || '创建失败');
                    return;
                }
                this.$message.success('创建成功');
                this.defaultBindMode = false;
                await this.fetchBindingDetail();
                this.pageMode = 'detail';
                this.$emit('bind-changed');
            } catch (e) {
                this.$message.error(e.message || '创建失败');
            } finally {
                this.submitLoading = false;
            }
        },
        confirmCandidate() {
            if (this.searchTab === 'id') {
                if (!this.searchResult) {
                    this.$message.error('请先查询艺人信息');
                    return;
                }
                this.newId = this.searchResult.id;
            } else {
                if (this.selectedResult < 0) {
                    this.$message.error('请选择要关联的艺人');
                    return;
                }
                this.newId = this.searchResults[this.selectedResult].id;
            }
            // 未关联状态弹确认关联框，已关联弹替换确认框
            if (this.isStatusBound) {
                this.replaceDialogVisible = true;
            } else {
                this.linkConfirmDialogVisible = true;
            }
        },
        confirmLink() {
            this.linkConfirmDialogVisible = false;
            this.finalConfirm();
        },
        async finalConfirm() {
            this.replaceDialogVisible = false;
            this.submitLoading = true;
            try {
                let currentResult;
                if (this.searchTab === 'id') {
                    currentResult = this.searchResult;
                } else {
                    currentResult = this.searchResults[this.selectedResult];
                }
                const params = {
                    opUserId: this.$store.state.userInfo?.id,
                    artistId: parseInt(this.artistId),
                    platformType: this.platformTypeCode
                };
                if (this.platformTypeCode === 3) {
                    // Apple Music
                    params.appleId = parseInt(currentResult.id);
                    params.idSource = 1; // 1=页面查询
                } else if (this.platformTypeCode === 4) {
                    // Spotify
                    params.spotifyId = currentResult.id;
                    params.spotifyName = currentResult.name;
                    params.idSource = 1; // 1=页面查询
                }
                const res = await relatePlatformArtist(params);
                if (!res.data.state) {
                    this.$message.error(res.data.msg);
                    return;
                }
                this.$message.success('关联成功');
                this.pageMode = 'detail';
                // 重新获取最新绑定详情
                await this.fetchBindingDetail();
            } catch (e) {
                this.$message.error('操作失败');
            } finally {
                this.submitLoading = false;
            }
        },
        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}`;
        },
        openUrl(url) {
            if (url) {
                window.open(url, '_blank');
            }
        },
        openCreateHomeDialog() {
            this.createHomeDialogVisible = true;
        },
        async createAndRelate() {
            const locale = this.displayLangCode;
            const name = this.searchKeyword || this.artistName;
            if (!locale) {
                return this.$message.error('未获取到专辑语言，请刷新页面后重试');
            }
            this.createHomeLoading = true;
            try {
                const res = await createRelAppleMusicArtist({
                    artistId: parseInt(this.artistId),
                    locale,
                    name
                });
                if (!res.data.state) {
                    return this.$message.error(res.data.msg || '创建失败');
                }
                this.$message.success('创建并关联成功');
                this.createHomeDialogVisible = false;
                this.pageMode = 'detail';
                await this.fetchBindingDetail();
                this.$emit('bind-changed');
            } catch (e) {
                this.$message.error('操作失败');
            } finally {
                this.createHomeLoading = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.platform-detail-page {
    min-height: calc(100vh - 120px);
    background: #f5f7fa;
    margin: -24px -24px -24px;
    padding: 24px;
}

.sync-error-bar {
    margin-bottom: 16px;
}

.info-card {
    ::v-deep {
        .el-card {
            border: none;
            border-radius: 4px;
            box-shadow: none;
            background: #fff;
            border: 1px solid #ebeef5;
        }

        .el-card__body {
            padding: 20px;
        }
    }

    .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }
}

.platform-title-row {
    display: flex;
    align-items: center;
    gap: 12px;
}

.platform-logo {
    width: 40px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    line-height: 0;
    vertical-align: middle;

    &.spotify-logo {
        background: #000000;
        border-radius: 9px;

        ::v-deep {
            .svg-icon {
                width: 36px !important;
                height: 36px !important;
                font-size: 36px !important;
            }

            svg {
                width: 36px !important;
                height: 36px !important;
            }
        }
    }

    ::v-deep {
        .svg-icon {
            width: 40px !important;
            height: 40px !important;
            font-size: 40px !important;
            display: block;
            line-height: 1;
        }

        svg {
            display: block;
            width: 40px !important;
            height: 40px !important;
        }
    }
}

.platform-name-text {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
}

.badge {
    font-size: 14px;
    font-weight: bold;
    padding: 3px 8px;
    border-radius: 10px;

    &.badge-bound {
        background: #f0f9eb;
        color: #67c23a;
    }

    &.badge-unbound {
        background: #f4f4f5;
        color: #909399;
    }

    &.badge-special {
        background: #fdf6ec;
        color: #e6a23c;
    }
}

.info-rows {
    font-size: 14px;
}

.info-row-two-col {
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;

    &:last-child {
        margin-bottom: 0;
    }
}

.info-item {
    flex: 1;
    display: flex;
    align-items: center;
}

.info-label {
    color: #909399;
    margin-right: 12px;
    flex-shrink: 0;
}

.info-value {
    color: #303133;

    &.link {
        color: #409eff;
        cursor: pointer;
    }
}

.copy-btn {
    color: #409eff;
    margin-left: 12px;
    padding: 0;
    font-size: 14px;
}

.card-title {
    font-size: 20px;
    font-weight: 600;
    color: #303133;
    padding: 0;
}

.platform-info-section {
    margin-top: 20px;
}

.section-title {
    font-size: 20px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 20px;
}

.unsync-list {
    font-size: 14px;
}

.unsync-row {
    padding: 14px 0;
    border-bottom: none;
}

.unsync-label {
    color: #303133;
    display: inline-block;
    width: 120px;
}

.unsync-value {
    color: #c0c4cc;
}

.unsync-hint-box {
    border: 1px dashed #dcdfe6;
    border-radius: 8px;
    background: #fafbfc;
    padding: 80px 20px;
    text-align: center;
}

.unsync-hint-title {
    font-size: 18px;
    font-weight: 500;
    color: #606266;
    margin-bottom: 24px;
}

.unsync-hint-desc {
    font-size: 16px;
    color: #909399;
    line-height: 1.6;
}

.empty-hint {
    text-align: center;
    padding: 40px 0;
    color: #909399;
}

.profile-content {
    display: flex;
    gap: 20px;
}

.avatar-wrapper {
    width: 100px;
    height: 100px;
    flex-shrink: 0;
}

.profile-avatar {
    width: 100px !important;
    height: 100px !important;
    border-radius: 12px !important;
    cursor: pointer;
}

.avatar-placeholder.profile-placeholder {
    width: 100px;
    height: 100px;
    border-radius: 12px;
    flex-direction: column;
    font-size: 14px;

    i {
        font-size: 32px;
        margin-bottom: 4px;
    }

    .placeholder-text {
        font-size: 12px;
        color: #c0c4cc;
        line-height: 1;
    }
}

.profile-info {
    flex: 1;
}

.artist-name {
    font-size: 24px;
    font-weight: bold;
    line-height: 1.2;
    color: #303133;
}

.artist-desc {
    margin-top: 12px;
    font-size: 16px;
    color: #606266;
    line-height: 1.6;
}

.tags-line {
    margin-top: 16px;
    font-size: 16px;
    color: #909399;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .tags-label {
        flex-shrink: 0;
    }

    .info-tag {
        display: inline-flex;
        align-items: center;
        padding: 4px 12px;
        // background: #f5f7fa;
        border-radius: 16px;
        font-size: 14px;
        color: #606266;
    }
}

.tags-line-small {
    margin-top: 6px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-width: 0;
}

.tags-label-small {
    font-size: 13px;
    color: #909399;
    flex-shrink: 0;
}

.info-tag-small {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    // background: #f5f7fa;
    border-radius: 4px;
    font-size: 12px;
    color: #606266;
    line-height: 1.5;
    max-width: 100%;
    overflow: hidden;

    ::v-deep .el-tag {
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        word-break: break-all;
        height: auto;
        line-height: 1.5;
    }
}

.actions-bar {
    display: flex;
    justify-content: flex-end;
    gap: 14px;
    padding: 24px 0 0;

    .el-button {
        min-width: 110px;
        border-radius: 6px;
        font-size: 15px;

        &--default {
            border-color: #dcdfe6;
            color: #606266;
            background: #fff;
        }

        &--danger.is-plain {
            color: #f56c6c;
            background: #fff;
            border-color: #fbc4c4;

            &:hover,
            &:focus {
                background: #fef0f0;
                color: #f56c6c;
                border-color: #fbc4c4;
            }
        }

        &.is-plain:not(.el-button--primary):not(.el-button--danger) {
            border-color: #dcdfe6;
            color: #606266;
            background: #fff;

            &:hover,
            &:focus {
                background: #ecf5ff;
                color: #409eff;
                border-color: #b3d8ff;
            }
        }

        &--primary {
            background: #409eff;
            border-color: #409eff;

            &:hover,
            &:focus {
                background: #66b1ff;
                border-color: #66b1ff;
            }
        }
    }
}

.avatar {
    width: 50px;
    height: 50px;
    border-radius: 6px;
}

.avatar-placeholder {
    width: 50px;
    height: 50px;
    border-radius: 6px;
    background: #f0f2f5;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 28px;
}

.avatar-placeholder-small {
    width: 50px;
    height: 50px;
    border-radius: 6px;
    background: #f0f2f5;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 28px;
}

.search-tabs {
    display: flex;
    margin-bottom: 24px;
}

.tab-item {
    padding: 12px 28px;
    font-size: 16px;
    cursor: pointer;
    border: 1px solid #dcdfe6;
    background: #fff;
    color: #303133;
    font-weight: 500;

    &:first-child {
        border-radius: 4px 0 0 4px;
        border-right: none;
    }

    &:last-child {
        border-radius: 0 4px 4px 0;
    }

    &.active {
        background: #409eff;
        color: #fff;
        border-color: #409eff;
    }
}

.search-row {
    display: flex;
    gap: 12px;
}

.grow {
    flex: 1;
}

.search-input {
    flex: 1;

    ::v-deep {
        .el-input__inner {
            height: 48px;
            line-height: 48px;
            font-size: 15px;
            border-radius: 4px;
        }
    }
}

.search-btn {
    width: 80px;
    height: 48px;
    font-size: 15px;
    border-radius: 4px;
}

.error-warn {
    padding: 14px 16px;
    background: #fdf6ec;
    border: 1px solid #faecd8;
    border-radius: 4px;
    margin-top: 16px;
}

.error-text {
    color: #e6a23c;
    font-size: 14px;
    margin-bottom: 12px;

    i {
        margin-right: 6px;
    }
}

.error-actions {
    display: flex;
    gap: 10px;
}

.create-home-btn {
    background: #e6a23c !important;
    border-color: #e6a23c !important;
    color: #fff !important;

    &:hover,
    &:focus {
        background: #ff8833 !important;
        border-color: #ff8833 !important;
        color: #fff !important;
    }
}

.no-data {
    text-align: center;
    padding: 48px 0;
    color: #909399;
    font-size: 14px;
}

.create-home-hint {
    margin-top: 20px;
}

.create-hint-box {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-radius: 4px;
    background: rgb(244, 244, 245);
}

.create-hint-icon {
    font-size: 16px;
    margin-right: 8px;
}

.create-hint-text {
    font-size: 14px;
    color: #303133;
    line-height: 1.5;
}

.results-list {
    margin-top: 20px;
}

.create-home-hint-link {
    font-size: 14px;
    color: #606266;
    margin-bottom: 12px;
    cursor: pointer;

    &:hover .create-link-text {
        text-decoration: underline;
    }

    .create-link-text {
        color: #e6a23c;
        font-weight: 500;
    }
}

.result-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid #e4e7ed;
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 12px;
    cursor: pointer;
    transition: all 0.2s;
    overflow: hidden;
    width: 100%;
    box-sizing: border-box;

    &:hover {
        border-color: #b3d8ff;
    }

    &.active {
        border-color: #409eff;
        background: #ecf5ff;
    }
}

.result-left-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    min-width: 0;
}

.curated-item .result-left-wrapper {
    align-items: center;
}

.result-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
}

.curated-item {
    border: 2px solid #409eff !important;
    background: #ecf5ff;
    padding: 20px;
    flex-direction: column;
    align-items: stretch;
    border-radius: 8px;
}

.curated-item .result-left {
    align-items: center;
    gap: 16px;
}

.candidate-avatar .el-image {
    width: 50px !important;
    height: 50px !important;
    border-radius: 6px;
}

.curated-item .candidate-avatar {
    width: 50px;
    height: 50px;
    border-radius: 6px;
    overflow: hidden;
    flex-shrink: 0;
}

.curated-item .candidate-avatar .el-image {
    width: 50px !important;
    height: 50px !important;
    border-radius: 6px;
}

.curated-item .candidate-avatar .avatar-placeholder-small {
    width: 50px;
    height: 50px;
    border-radius: 6px;
}

.curated-item .custom-radio {
    margin-top: 17px;
}

.curated-item .candidate-name {
    font-size: 18px;
    word-break: break-all;
}

.curated-item .candidate-sub {
    font-size: 14px;
    margin-bottom: 8px;
    word-break: break-all;
}

.candidate-name {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    word-break: break-all;
    min-width: 0;
}

.candidate-sub {
    font-size: 13px;
    color: #909399;
    word-break: break-all;
    min-width: 0;
}

.candidate-info {
    flex: 1;
    min-width: 0;
    overflow: hidden;
}

.custom-radio {
    width: 16px;
    height: 16px;
    border: 1px solid #dcdfe6;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    background: #fff;
    box-sizing: border-box;

    &.checked {
        border-color: #409eff;
        background: #409eff;
    }

    .radio-dot {
        width: 4px;
        height: 4px;
        background: #fff;
        border-radius: 50%;
    }
}

.open-home-btn {
    color: #409eff !important;
    font-size: 14px;
    padding: 0;
    font-weight: 500;
    flex-shrink: 0;
    margin-left: 12px;
}

.load-more-wrapper {
    margin-top: 20px;
    display: flex;
    justify-content: center;

    .el-button {
        min-width: 120px;
    }
}

.no-more-wrapper {
    margin-top: 20px;
    text-align: center;
    color: #909399;
    font-size: 14px;
}

.bind-card {
    ::v-deep {
        .el-card {
            border: none;
            border-radius: 4px;
            box-shadow: none;
            background: #fff;
            border: 1px solid #ebeef5;
        }

        .el-card__body {
            padding: 24px;
        }
    }
}

.candidate-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #e4e7ed;
    border-radius: 6px;
    padding: 12px 16px;
}

.candidate-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.radio-wrapper {
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.radio {
    width: 16px;
    height: 16px;
    border: 1px solid #409eff;
    border-radius: 50%;
    background: #409eff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;

    &.checked {
        &::after {
            content: '';
            width: 4px;
            height: 4px;
            background: #fff;
            border-radius: 50%;
        }
    }
}

.candidate-avatar {
    width: 50px;
    height: 50px;
    border-radius: 6px;
    overflow: hidden;
    background: #f0f2f5;
    flex-shrink: 0;
}

.candidate-name {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
}

.candidate-sub {
    font-size: 13px;
    color: #909399;
}

.candidate-fans {
    font-size: 13px;
    color: #909399;
}

.inline-note {
    font-size: 12px;
    color: #909399;
    margin-top: 8px;
}

.dialog-content {
    font-size: 14px;
    line-height: 1.8;

    strong {
        color: #409eff;
    }
}

.relink-dialog {
    top: 30%;
}

.m-left_8 {
    margin-left: 8px;
}

.m-bottom_16 {
    margin-bottom: 16px;
}

.m-top_16 {
    margin-top: 16px;
}

.m-top_24 {
    margin-top: 24px;
}

.candidate-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
    flex-wrap: wrap;
    min-width: 0;
}

.curated-tag {
    display: inline-flex;
    align-items: center;
    padding: 0 8px;
    height: 28px;
    line-height: 22px;
    border-radius: 4px;
    background: #fef0f0;
    border: 1px solid #fbc4c4;
    color: #f56c6c;
    font-weight: normal;
    vertical-align: middle;

    ::v-deep {
        .el-tag__content {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 0;
        }

        .svg-icon {
            width: 16px !important;
            height: 16px !important;
            font-size: 16px !important;
            color: #f56c6c;
        }
    }

    .tag-text {
        margin-left: 8px;
        font-size: 13px;
    }
}

.curated-warning {
    margin-top: 16px;
    margin-left: 92px;
    padding: 8px 16px;
    background: #fdf6ec;
    border-radius: 8px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 14px;
    color: #e6a23c;
    word-break: break-all;
    line-height: 1.6;

    .warning-icon {
        font-size: 18px;
        flex-shrink: 0;
        margin-top: 0;
        color: #e6a23c;
    }
}

.profile-content-wrapper {
    // background: #ecf5ff;
    // border: 2px solid #409eff;
    border-radius: 6px;
    padding: 16px;
}

.artist-name-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.detail-curated-tag {
    height: 24px;
    padding: 0 8px;

    .tag-text {
        font-size: 13px;
    }
}

.detail-curated-warning {
    width: calc(100% - 120px);
    margin-top: 16px;
    margin-left: 120px;
    font-size: 14px;
}

.create-main-page {
    padding: 60px 40px;
    text-align: center;

    .create-title {
        font-size: 20px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 24px;
    }

    .create-tip {
        font-size: 14px;
        color: #909399;
        line-height: 1.8;
    }
}
</style>
